// Accountability Groups (Phase 1)
//
// Cross-forum groups with a propose -> unanimous-approval -> accept invite flow
// and read-only, default-deny feeds of shared prayers, grow goals, and members'
// latest snapshots.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Plus,
  MessageSquare,
  Crown,
  Heart,
  Target,
  FileText,
  UserPlus,
  UserMinus,
  Search,
  X,
  Clock,
  LogOut,
  Trash2,
  Loader2,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { accountabilityGroupsAPI, messagingAPI, usersAPI } from '../api/client';
import { useAuth } from '../context/useAuth';
import type {
  AccountabilityGroup,
  AccountabilityGroupDetail,
  AccountabilityPendingInvitations,
  UserListItem,
} from '../types';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';
import MicButton from '../components/MicButton';
import { useVisibilityPolling } from '../hooks/useVisibilityPolling';

function apiError(error: unknown, fallback: string) {
  const responseError = error as { response?: { data?: { error?: string; detail?: string } } };
  return responseError.response?.data?.error || responseError.response?.data?.detail || fallback;
}

export default function AccountabilityGroups() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState<AccountabilityGroup[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<AccountabilityGroupDetail | null>(null);
  const [pending, setPending] = useState<AccountabilityPendingInvitations>({
    to_approve: [],
    to_accept: [],
  });
  const [actionError, setActionError] = useState('');

  // Create-group modal
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);

  // Invite people-picker
  const [showInvite, setShowInvite] = useState(false);
  const [directory, setDirectory] = useState<UserListItem[]>([]);
  const [inviteSearch, setInviteSearch] = useState('');

  // Confirm modal
  const [confirm, setConfirm] = useState<{ message: string; action: () => void } | null>(null);

  const [showChatPicker, setShowChatPicker] = useState(false);
  const [openingChat, setOpeningChat] = useState(false);

  const groupsPolling = useVisibilityPolling({
    fetcher: async () => Promise.all([
      accountabilityGroupsAPI.list(),
      accountabilityGroupsAPI.pendingInvitations(),
    ]),
    onSuccess: ([nextGroups, nextPending]) => {
      setGroups(nextGroups);
      setPending(nextPending);
      setSelectedId((current) => {
        if (current !== null && nextGroups.some((group) => group.id === current)) return current;
        return nextGroups[0]?.id ?? null;
      });
    },
    intervalMs: 8000,
  });

  const detailPolling = useVisibilityPolling({
    fetcher: () => accountabilityGroupsAPI.getDetail(selectedId as number),
    onSuccess: setDetail,
    intervalMs: 8000,
    enabled: selectedId !== null,
    refreshKey: selectedId,
  });

  useEffect(() => {
    if (selectedId === null) setDetail(null);
  }, [selectedId]);

  const refreshAll = async () => {
    await Promise.all([groupsPolling.refresh(true), detailPolling.refresh(true)]);
  };

  const openGroupChat = async (groupId: number) => {
    if (openingChat) return;
    setOpeningChat(true);
    try {
      const thread = await messagingAPI.createThread({
        thread_type: 'group',
        kind: 'group',
        accountability_group: groupId,
      });
      setShowChatPicker(false);
      navigate(`/messages?thread=${thread.id}`);
    } catch (error) {
      toast.error(apiError(error, 'Could not open the group chat.'));
    } finally {
      setOpeningChat(false);
    }
  };

  const handleNewGroupMessage = () => {
    if (groups.length === 0) {
      toast.error('Join or create an accountability group first.');
      return;
    }
    if (groups.length === 1) {
      void openGroupChat(groups[0].id);
      return;
    }
    setShowChatPicker(true);
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const created = await accountabilityGroupsAPI.create(newName.trim(), newDesc.trim());
      setShowCreate(false);
      setNewName('');
      setNewDesc('');
      setGroups((current) => [created, ...current.filter((group) => group.id !== created.id)]);
      setDetail(created);
      setSelectedId(created.id);
      toast.success('Group created.');
    } catch {
      toast.error('Could not create group.');
    } finally {
      setCreating(false);
    }
  };

  const openInvite = async () => {
    setShowInvite(true);
    if (directory.length === 0) {
      try {
        setDirectory(await usersAPI.getDirectory());
      } catch {
        setDirectory([]);
      }
    }
  };

  const handlePropose = async (inviteeId: number) => {
    if (selectedId === null) return;
    try {
      await accountabilityGroupsAPI.propose(selectedId, inviteeId);
      toast.success('Invitation proposed — awaiting member approval.');
      setShowInvite(false);
      setInviteSearch('');
      await refreshAll();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Could not propose invitation.');
    }
  };

  const handleApprove = async (id: number) => {
    setActionError('');
    try {
      await accountabilityGroupsAPI.approveInvitation(id);
      setPending((current) => ({
        ...current,
        to_approve: current.to_approve.filter((invitation) => invitation.id !== id),
      }));
      toast.success('Approved.');
      await refreshAll();
    } catch (error) {
      setActionError(apiError(error, 'Could not approve this invitation.'));
    }
  };

  const handleAccept = async (id: number) => {
    setActionError('');
    try {
      const joinedGroup = await accountabilityGroupsAPI.acceptInvitation(id);
      setPending((current) => ({
        ...current,
        to_accept: current.to_accept.filter((invitation) => invitation.id !== id),
      }));
      setGroups((current) => [
        joinedGroup,
        ...current.filter((group) => group.id !== joinedGroup.id),
      ]);
      setDetail(joinedGroup);
      setSelectedId(joinedGroup.id);
      toast.success('You joined the group.');
    } catch (error) {
      setActionError(apiError(error, 'Could not accept this invitation.'));
    }
  };

  const handleDecline = async (id: number) => {
    setActionError('');
    try {
      await accountabilityGroupsAPI.declineInvitation(id);
      setPending((current) => ({
        ...current,
        to_accept: current.to_accept.filter((invitation) => invitation.id !== id),
      }));
    } catch (error) {
      setActionError(apiError(error, 'Could not decline this invitation.'));
    }
  };

  const handleLeave = (id: number) => {
    setConfirm({
      message: 'Leave this group? Your shared prayers and goals here will be revoked.',
      action: async () => {
        await accountabilityGroupsAPI.leave(id);
        setConfirm(null);
        setSelectedId(null);
        await groupsPolling.refresh(true);
        toast.success('You left the group.');
      },
    });
  };

  const handleDisband = (id: number) => {
    setConfirm({
      message: 'Disband this group for everyone? This cannot be undone.',
      action: async () => {
        await accountabilityGroupsAPI.disband(id);
        setConfirm(null);
        setSelectedId(null);
        await groupsPolling.refresh(true);
        toast.success('Group disbanded.');
      },
    });
  };

  const handleRemove = (id: number, userId: number) => {
    setConfirm({
      message: 'Remove this member? Their shares with this group will be revoked.',
      action: async () => {
        await accountabilityGroupsAPI.removeMember(id, userId);
        setConfirm(null);
        await refreshAll();
      },
    });
  };

  const memberIdsInDetail = useMemo(
    () => new Set(detail?.members.map((m) => m.id) ?? []),
    [detail],
  );

  const filteredDirectory = useMemo(() => {
    const q = inviteSearch.trim().toLowerCase();
    return directory
      .filter((u) => u.id !== user?.id && !memberIdsInDetail.has(u.id))
      .filter((u) => !q || u.full_name.toLowerCase().includes(q));
  }, [directory, inviteSearch, memberIdsInDetail, user?.id]);

  if (groupsPolling.isInitialLoading) {
    return (
      <div className="space-y-5" role="status" aria-label="Loading accountability groups">
        <div className="h-9 w-64 animate-pulse rounded-lg bg-gray-200" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-3">
            {[0, 1, 2].map((item) => (
              <div key={item} className="h-20 animate-pulse rounded-2xl bg-white shadow-sm" />
            ))}
          </div>
          <div className="h-72 animate-pulse rounded-2xl bg-white shadow-sm lg:col-span-2" />
        </div>
        <span className="sr-only">Loading accountability groups…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">
            Accountability Groups
          </h1>
          <p className="text-gray-600">
            Your consistent circle for prayers, grow goals, and snapshots. Nothing is
            shared by default — you choose what each group sees.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleNewGroupMessage}
            disabled={openingChat}
            className="inline-flex items-center gap-2 px-5 py-3 border border-navy text-navy rounded-xl hover:bg-navy hover:text-white transition font-semibold disabled:opacity-50"
          >
            {openingChat ? <Loader2 size={20} className="animate-spin" /> : <MessageSquare size={20} />}
            New Group Message
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-5 py-3 bg-navy text-white rounded-xl hover:bg-navy-dark transition font-semibold"
          >
            <Plus size={20} />
            Create Group
          </button>
        </div>
      </div>

      {(Boolean(groupsPolling.error) || Boolean(actionError)) && (
        <div role="alert" className="flex items-start justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 shrink-0" size={17} />
            <span>{actionError || apiError(groupsPolling.error, 'Could not load your accountability groups.')}</span>
          </div>
          {Boolean(groupsPolling.error) && (
            <button
              type="button"
              onClick={() => void groupsPolling.refresh()}
              className="inline-flex shrink-0 items-center gap-1 font-semibold hover:underline"
            >
              <RefreshCw size={14} /> Retry
            </button>
          )}
        </div>
      )}

      {/* Invitations awaiting me */}
      {(pending.to_accept.length > 0 || pending.to_approve.length > 0) && (
        <div className="space-y-3">
          {pending.to_accept.map((inv) => (
            <div
              key={inv.id}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-green-50 border border-green-200 rounded-xl p-4"
            >
              <div className="flex items-center gap-2 text-sm text-green-800">
                <UserPlus size={16} /> You're invited to join{' '}
                <strong>{inv.group_name}</strong>.
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleAccept(inv.id)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
                >
                  Accept
                </button>
                <button
                  onClick={() => handleDecline(inv.id)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm font-medium"
                >
                  Decline
                </button>
              </div>
            </div>
          ))}
          {pending.to_approve.map((inv) => (
            <div
              key={inv.id}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gold/10 border border-gold/30 rounded-xl p-4"
            >
              <div className="flex items-center gap-2 text-sm text-navy">
                <Clock size={16} className="text-gold" /> {inv.proposed_by_name} proposed inviting{' '}
                <strong>{inv.invitee_name}</strong> to {inv.group_name}.
              </div>
              <button
                onClick={() => handleApprove(inv.id)}
                className="px-4 py-2 bg-gold text-white rounded-lg hover:bg-gold-dark transition text-sm font-medium"
              >
                Approve
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Group list */}
        <div className="space-y-2">
          {groups.length === 0 && (
            <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
              <Users className="mx-auto text-gray-300" size={48} />
              <p className="text-gray-500 mt-2 text-sm">
                You're not in any accountability groups yet.
              </p>
            </div>
          )}
          {groups.map((g) => (
            <button
              key={g.id}
              onClick={() => {
                if (selectedId !== g.id) setDetail(null);
                setSelectedId(g.id);
              }}
              aria-pressed={selectedId === g.id}
              aria-label={`Select ${g.name}, ${g.member_count} of ${g.max_members} members`}
              className={`w-full rounded-xl px-4 py-3 text-left transition shadow-sm ${
                selectedId === g.id
                  ? 'bg-navy/5 ring-1 ring-navy/30'
                  : 'bg-white hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-navy">{g.name}</span>
                {g.is_owner && <Crown size={14} className="text-gold" />}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {g.member_count}/{g.max_members} members
              </div>
            </button>
          ))}
        </div>

        {/* Group hub */}
        <div className="lg:col-span-2">
          {selectedId === null ? (
            <div className="flex min-h-72 items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center shadow-sm">
              <div className="max-w-sm">
                <Users className="mx-auto text-gray-300" size={44} />
                <h2 className="mt-3 text-lg font-semibold text-navy">
                  {groups.length === 0 ? 'Create your first circle' : 'Select a group'}
                </h2>
                <p className="mt-1 text-sm leading-6 text-gray-500">
                  {groups.length === 0
                    ? 'Start a private accountability group, then invite members you trust.'
                    : 'Choose a group to see its members and shared updates.'}
                </p>
              </div>
            </div>
          ) : detailPolling.error && !detail ? (
            <div role="alert" className="flex min-h-72 items-center justify-center rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
              <div>
                <AlertCircle className="mx-auto text-red-500" size={36} />
                <p className="mt-3 font-semibold text-red-800">Could not load this group.</p>
                <button
                  type="button"
                  onClick={() => void detailPolling.refresh()}
                  className="mt-4 inline-flex items-center gap-2 rounded-lg bg-navy px-4 py-2 text-sm font-semibold text-white"
                >
                  <RefreshCw size={15} /> Try again
                </button>
              </div>
            </div>
          ) : detailPolling.isInitialLoading || !detail ? (
            <div className="space-y-4" role="status" aria-label="Loading selected group">
              <div className="h-32 animate-pulse rounded-2xl bg-white shadow-sm" />
              <div className="h-48 animate-pulse rounded-2xl bg-white shadow-sm" />
              <span className="sr-only">Loading selected group…</span>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-bold text-navy">{detail.name}</h2>
                    {detail.description && (
                      <p className="mt-1 text-sm text-gray-600">{detail.description}</p>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={openInvite}
                      className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm"
                    >
                      <UserPlus size={16} /> Invite
                    </button>
                    {detail.is_owner ? (
                      <button
                        onClick={() => handleDisband(detail.id)}
                        className="flex items-center gap-1.5 px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition text-sm"
                      >
                        <Trash2 size={16} /> Disband
                      </button>
                    ) : (
                      <button
                        onClick={() => handleLeave(detail.id)}
                        className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm"
                      >
                        <LogOut size={16} /> Leave
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Members */}
              <Section icon={<Users size={18} />} title={`Members (${detail.members.length})`}>
                <div className="space-y-2">
                  {detail.members.map((m) => (
                    <div
                      key={m.id}
                      className="flex items-center justify-between bg-off-white rounded-xl px-4 py-2.5"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-800">{m.name}</span>
                        {m.role === 'owner' && <Crown size={12} className="text-gold" />}
                        {m.is_self && <span className="text-xs text-gold">(You)</span>}
                        {m.snapshot && (
                          <button
                            type="button"
                            onClick={() => navigate(`/snapshots/${m.snapshot!.id}?from=accountability`)}
                            className="inline-flex items-center gap-1 text-xs text-navy hover:underline"
                          >
                            <FileText size={11} /> {m.snapshot.month_display}
                          </button>
                        )}
                      </div>
                      {detail.is_owner && !m.is_self && (
                        <button
                          onClick={() => handleRemove(detail.id, m.id)}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                          title="Remove member"
                        >
                          <UserMinus size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </Section>

              {/* Snapshot sharing toggle */}
              <Section icon={<FileText size={18} />} title="My snapshot sharing">
                <SingleGroupSnapshotToggle
                  groupId={detail.id}
                  enabled={detail.my_share_snapshot}
                  onChange={refreshAll}
                />
              </Section>

              {/* Shared prayers */}
              <Section
                icon={<Heart size={18} />}
                title={`Shared prayers (${detail.shared_prayers.length})`}
              >
                {detail.shared_prayers.length === 0 ? (
                  <p className="text-sm text-gray-400">Nothing shared yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {detail.shared_prayers.map((p) => (
                      <li key={p.id} className="bg-off-white rounded-xl px-4 py-3">
                        <div className="text-sm font-medium text-gray-800">{p.title}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{p.content}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              {/* Shared grow goals */}
              <Section
                icon={<Target size={18} />}
                title={`Shared grow goals (${detail.shared_grow_goals.length})`}
              >
                {detail.shared_grow_goals.length === 0 ? (
                  <p className="text-sm text-gray-400">Nothing shared yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {detail.shared_grow_goals.map((c) => (
                      <li key={c.id} className="bg-off-white rounded-xl px-4 py-3">
                        <div className="text-sm text-gray-800">{c.description}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {c.user_name} · {c.status}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </div>
          )}
        </div>
      </div>

      {/* Create-group modal */}
      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="Create accountability group">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Group name"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-navy focus:border-transparent outline-none"
          />
          <div className="relative mt-3">
            <textarea
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="Description (optional)"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-12 focus:ring-2 focus:ring-navy focus:border-transparent outline-none"
              rows={3}
            />
            <MicButton
              value={newDesc}
              onChange={setNewDesc}
              className="absolute bottom-2 right-2"
            />
          </div>
          <button
            onClick={handleCreate}
            disabled={creating || !newName.trim()}
            className="mt-4 w-full py-3 bg-navy text-white rounded-lg font-medium hover:bg-navy-dark transition disabled:opacity-50"
          >
            {creating ? 'Creating…' : 'Create'}
          </button>
        </Modal>
      )}

      {/* Invite people-picker */}
      {showInvite && (
        <Modal onClose={() => setShowInvite(false)} title="Propose an invite">
          <p className="mb-3 text-xs text-gray-500">
            Everyone already in the group must approve before the person can join.
          </p>
          <div className="relative mb-3">
            <Search size={14} className="absolute left-3 top-3 text-gray-400" />
            <input
              value={inviteSearch}
              onChange={(e) => setInviteSearch(e.target.value)}
              placeholder="Search members…"
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 focus:ring-2 focus:ring-navy focus:border-transparent outline-none"
            />
          </div>
          <div className="max-h-72 space-y-1 overflow-y-auto">
            {filteredDirectory.map((u) => (
              <button
                key={u.id}
                onClick={() => handlePropose(u.id)}
                className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left hover:bg-off-white transition"
              >
                <span className="text-sm text-gray-800">{u.full_name}</span>
                <UserPlus size={14} className="text-navy" />
              </button>
            ))}
            {filteredDirectory.length === 0 && (
              <p className="px-3 py-4 text-center text-sm text-gray-400">No matches.</p>
            )}
          </div>
        </Modal>
      )}

      {showChatPicker && (
        <Modal onClose={() => setShowChatPicker(false)} title="Choose a group chat">
          <p className="mb-3 text-sm text-gray-500">
            You are in more than one accountability group. Pick which chat to open.
          </p>
          <div className="max-h-72 space-y-1 overflow-y-auto">
            {groups.map((group) => (
              <button
                key={group.id}
                type="button"
                disabled={openingChat}
                onClick={() => void openGroupChat(group.id)}
                className="flex w-full items-center justify-between rounded-lg px-3 py-3 text-left hover:bg-off-white transition disabled:opacity-50"
              >
                <span>
                  <span className="block text-sm font-semibold text-navy">{group.name}</span>
                  <span className="block text-xs text-gray-500">
                    {group.member_count} {group.member_count === 1 ? 'member' : 'members'}
                  </span>
                </span>
                <MessageSquare size={16} className="text-navy shrink-0" />
              </button>
            ))}
          </div>
        </Modal>
      )}

      {confirm && (
        <ConfirmModal
          isOpen={true}
          title="Please confirm"
          message={confirm.message}
          onConfirm={confirm.action}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-2xl shadow-sm p-6">
      <h3 className="mb-3 flex items-center gap-2 font-semibold text-navy">
        {icon} {title}
      </h3>
      {children}
    </div>
  );
}

function SingleGroupSnapshotToggle({
  groupId,
  enabled,
  onChange,
}: {
  groupId: number;
  enabled: boolean;
  onChange: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const toggle = async (value: boolean) => {
    setSaving(true);
    try {
      await accountabilityGroupsAPI.setShareSnapshot(groupId, value);
      onChange();
    } catch {
      toast.error('Could not update snapshot sharing.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <label className="flex items-center gap-2 text-sm text-gray-700">
      <input
        type="checkbox"
        checked={enabled}
        disabled={saving}
        onChange={(e) => toggle(e.target.checked)}
        className="w-4 h-4 rounded border-gray-300 text-navy focus:ring-navy"
      />
      Share my latest finalized snapshot with this group
      {saving && <Loader2 size={12} className="animate-spin text-gray-400" />}
    </label>
  );
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-2xl bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-navy">{title}</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition">
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
